import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Container } from '@mui/material';
import { NavigationProvider } from './contexts/NavigationContext';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './layouts/AppLayout';
import DashboardPage from './pages/DashboardPage';
import ResidentsPage from './pages/ResidentsPage';
import LoginPage from './pages/LoginPage';
import AdmissionsPage from './pages/AdmissionsPage';
import ClinicalNotesPage from './pages/ClinicalNotesPage';
import MdsAssessmentPage from './pages/MdsAssessmentPage';
import HospiceAssessmentPage from './pages/HospiceAssessmentPage';
import BillingPage from './pages/BillingPage';
import TenantManagementPage from './pages/TenantManagementPage';
import SuperAdminLoginPage from './pages/SuperAdminLoginPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ClientManagementPage from './pages/ClientManagementPage';
import ClientFacilitiesPage from './pages/ClientFacilitiesPage';
import PatientDiagnosisPage from './pages/PatientDiagnosisPage';
import OrderMedicationPage from './pages/OrderMedicationPage';
import UserManagementPage from './pages/UserManagementPage';
import ProfilePage from './pages/ProfilePage';

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <NavigationProvider>
          <Routes>
            {/* Public routes - no auth required */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />

            {/* Protected routes requiring authentication */}
            <Route path="/" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <DashboardPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <DashboardPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/residents" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <ResidentsPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/clinical-notes" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <ClinicalNotesPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admissions" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <AdmissionsPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/patient-diagnosis" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <PatientDiagnosisPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/emr" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <ClinicalNotesPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/mds-assessment" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <MdsAssessmentPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/hospice-assessment" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <HospiceAssessmentPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <BillingPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/medication-orders" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <OrderMedicationPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/tenant-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <TenantManagementPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            {/* Super Admin Routes */}
            <Route path="/super-admin/dashboard" element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <SuperAdminDashboard />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/clients" element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <ClientManagementPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/clients/:clientId/facilities" element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'CLIENT_ADMIN']}>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <ClientFacilitiesPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'CLIENT_ADMIN']}>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <UserManagementPage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <AppLayout>
                  <Container maxWidth={false} sx={{ maxWidth: 'unset', px: 3, py: 2 }}>
                    <ProfilePage />
                  </Container>
                </AppLayout>
              </ProtectedRoute>
            } />
          </Routes>
        </NavigationProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;